import React from "react";
import { FaSearch, FaArrowDown, FaArrowUp, FaFilter, FaChevronDown } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { endAt, onValue, orderByKey, query, ref, startAt } from "firebase/database";
import dayjs from "dayjs";
import "dayjs/locale/el";
import { database } from "../firebaseConfig";
import { setEndDate, setFilters, setReports, setStartDate } from "../redux/slices/periodReports";
import { setLoading } from "../redux/slices/ui";
import PageHeader from "../components/ui/PageHeader";
import DateRangeForm from "../components/ui/DateRangeForm";
import NoData from "../components/ui/NoData";
import { eur } from "../utils/format";

dayjs.locale("el");

const PeriodReports = () => {
  const dispatch = useDispatch();
  const { startDate, endDate, reports, filters } = useSelector((state) => state.periodReports);
  const [openCard, setOpenCard] = React.useState([]);

  const groupReportsByMonth = (rows) =>
    rows.reduce((acc, report) => {
      const month = dayjs(report.date).format("MMMM YYYY");
      if (!acc[month]) acc[month] = { reports: [], totalIncomes: 0, totalExpenses: 0, total: 0 };
      acc[month].reports.push(report);
      acc[month].totalIncomes += report.totalIncomes || 0;
      acc[month].totalExpenses += report.totalExpenses || 0;
      acc[month].total += report.total || 0;
      return acc;
    }, {});

  const fetchReports = () => {
    if (!startDate || !endDate) return;
    dispatch(setLoading(true));
    const reportsQuery = query(ref(database, "daily-reports"), orderByKey(), startAt(startDate), endAt(endDate));
    onValue(reportsQuery, (snapshot) => {
      const data = snapshot.val();
      const list = data ? Object.entries(data).map(([date, report]) => ({ date, ...report })) : [];
      dispatch(setFilters(list.length === 0));
      dispatch(setReports(list));
      dispatch(setLoading(false));
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReports();
  };

  // Auto-load with the default date range when the screen opens.
  React.useEffect(() => {
    fetchReports();
  }, []);

  const toggle = (key) =>
    setOpenCard((arr) => (arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key]));

  const groupedReports = groupReportsByMonth(reports || []);
  const months = Object.keys(groupedReports);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PageHeader title="Περίοδος Απολογισμών">
        {months.length > 0 && !filters ? (
          <FaFilter className="cursor-pointer text-brand-600" onClick={() => dispatch(setFilters(true))} size={22} />
        ) : (
          <FaSearch className="cursor-pointer text-brand-600" onClick={handleSearch} size={22} />
        )}
      </PageHeader>

      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {filters && (
          <DateRangeForm
            startDate={startDate}
            endDate={endDate}
            onStart={(v) => dispatch(setStartDate(v))}
            onEnd={(v) => dispatch(setEndDate(v))}
            onSubmit={handleSearch}
          />
        )}

        {months.length === 0 ? (
          <NoData />
        ) : (
          <div className="space-y-3">
            {months.map((month) => {
              const g = groupedReports[month];
              const isOpen = openCard.includes(month);
              return (
                <div key={month} className="overflow-hidden rounded-2xl border border-brand-600/10 bg-white shadow-card">
                  <button
                    onClick={() => toggle(month)}
                    className="flex w-full items-center justify-between bg-brand-600 px-4 py-3 text-left text-white"
                  >
                    <h3 className="font-semibold capitalize">{month}</h3>
                    <FaChevronDown className={`transition-transform ${isOpen ? "rotate-180" : ""}`} size={14} />
                  </button>
                  <div className="flex justify-between px-4 py-3 text-sm">
                    <span className="flex items-center gap-1.5 font-medium text-emerald-600">
                      <FaArrowUp size={12} /> {eur(g.totalIncomes)}
                    </span>
                    <span className="flex items-center gap-1.5 font-medium text-rose-500">
                      <FaArrowDown size={12} /> {eur(g.totalExpenses)}
                    </span>
                    <span className="font-bold text-brand-700">{eur(g.total)}</span>
                  </div>
                  {isOpen && (
                    <ul className="divide-y divide-slate-100 border-t border-slate-100">
                      {g.reports.map((report) => (
                        <li key={report.date} className="flex items-center justify-between px-4 py-2.5 text-sm">
                          <span className="font-medium text-slate-700">{dayjs(report.date).format("DD/MM/YYYY")}</span>
                          <span className="text-emerald-600">{eur(report.totalIncomes)}</span>
                          <span className="text-rose-500">{eur(report.totalExpenses)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PeriodReports;
